import { useMemo } from "react";
import { LinkProps } from "next/link";
import {
  PROMPT_EDIT_MODELS,
  PromptEditModel,
} from "domains/assets/constants/promptEdit";
import {
  getIsAsset3D,
  getIsAssetInferenceTexture,
  getIsAssetSkybox,
  getIsAssetTexture,
  getIsAssetUpscaledTexture,
  getIsAssetVideo,
} from "domains/assets/utils/getIsType";
import {
  getIsModelFluxDev,
  getIsModelFluxPro,
  getIsModelFluxPro1_1,
  getIsModelFluxPro1_1Ultra,
  getIsModelGptImage,
} from "domains/models/utils";
import useRouter from "domains/navigation/hooks/useRouter";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { useUserContext } from "domains/user/contexts/UserProvider";
import {
  GetAssetsByAssetIdApiResponse,
  GetJobIdApiResponse,
  GetModelsByModelIdApiResponse,
  GetModelsInferencesByModelIdAndInferenceIdApiResponse,
  useGetModelsByModelIdQuery,
} from "infra/api/generated/api";
import _ from "lodash";

import { skipToken } from "@reduxjs/toolkit/query";

export function useAssetSearchSimilar(
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined
): LinkProps["href"] | undefined {
  const router = useRouter();
  const isSkybox = getIsAssetSkybox(asset);
  const isTexture = getIsAssetTexture(asset);
  const is3D = getIsAsset3D(asset);
  const isVideo = getIsAssetVideo(asset);

  const assetId = asset?.id;
  const searchTab = (() => {
    if (isSkybox) {
      return "skybox";
    }
    if (isTexture) {
      return "texture";
    }
    if (is3D) {
      return "3d";
    }
    if (isVideo) {
      return "video";
    }
    return "image";
  })();

  return useMemo(() => {
    if (!assetId) {
      return undefined;
    }

    return {
      pathname: router.pathname,
      query: {
        ..._.omit(router.query, "searchQuery", "openAssetId"),
        searchAssetId: assetId,
        searchTab,
      },
    };
  }, [assetId, router.pathname, router.query, searchTab]);
}

export function useAssetReloadParametersLink(
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined,
  {
    model,
    inference,
    job,
  }: {
    model?: GetModelsByModelIdApiResponse["model"];
    inference?: GetModelsInferencesByModelIdAndInferenceIdApiResponse["inference"];
    job?: GetJobIdApiResponse["job"];
  } = {}
):
  | {
      link: LinkProps["href"] | undefined;
      isLoading: boolean;
    }
  | undefined {
  const { selectedProject } = useTeamContext();
  const { isUnsigned } = useUserContext();
  const isTextureInference = getIsAssetInferenceTexture(asset);
  const isTextureUpscale = getIsAssetUpscaledTexture(asset);
  const isInference =
    (asset?.metadata.type ?? "").includes("inference") && !isTextureInference;
  const isUpscale = asset?.metadata.type === "upscale";
  const isSkyboxBase = asset?.metadata.type === "skybox-base-360";
  const isSkyboxUpscale = asset?.metadata.type === "upscale-skybox";
  const isRestyle = asset?.metadata.type === "restyle";
  const isPromptEdit = asset?.metadata.type === "image-prompt-editing";
  const isReframe = asset?.metadata.type === "reframe";
  const is3D =
    asset?.metadata.type === "img23d" || asset?.metadata.type === "txt23d";
  const isVideo =
    asset?.metadata.type === "img2video" ||
    asset?.metadata.type === "video2video" ||
    asset?.metadata.type === "txt2video";

  const linkAssetId = asset?.id;
  const linkModelId = model?.id ?? asset?.metadata.modelId;
  const linkInferenceId = inference?.id ?? asset?.metadata.inferenceId;
  const linkJobId = job?.jobId ?? asset?.metadata.parentJobId;

  const shouldFetchModel =
    !isUnsigned && asset?.metadata.type === "video2video" && !model;
  const { data: modelData, isLoading: isLoadingModel } =
    useGetModelsByModelIdQuery(
      shouldFetchModel && linkModelId
        ? { modelId: linkModelId, projectId: selectedProject.id }
        : skipToken
    );

  const isLoading = isLoadingModel;

  const link = useMemo(() => {
    if (isSkyboxBase && linkAssetId) {
      return {
        pathname: "/skyboxes/new",
        query: {
          importAssetId: linkAssetId,
        },
      };
    }

    if (isSkyboxUpscale && linkAssetId) {
      return {
        pathname: "/skyboxes/enhance",
        query: {
          restartEnhance: linkAssetId,
        },
      };
    }

    if (isInference && linkModelId && linkInferenceId) {
      return {
        pathname: "/images/new",
        query: {
          modelId: linkModelId,
          importAssetId: `${linkModelId}|${linkInferenceId}`,
        },
      };
    }

    if (isUpscale && linkAssetId) {
      return {
        pathname: "/enhance",
        query: {
          restartEnhance: linkAssetId,
        },
      };
    }

    if (isTextureInference && linkModelId && linkInferenceId) {
      return {
        pathname: "/textures/new",
        query: {
          modelId: linkModelId,
          importAssetId: `${linkModelId}|${linkInferenceId}`,
        },
      };
    }

    if (isTextureUpscale && linkAssetId) {
      return {
        pathname: "/textures/enhance",
        query: {
          restartEnhance: linkAssetId,
        },
      };
    }

    if (isRestyle && linkAssetId) {
      return {
        pathname: "/restyle",
        query: {
          restartRestyle: linkAssetId,
        },
      };
    }

    if (isPromptEdit && linkAssetId) {
      return {
        pathname: "/edit-with-prompts",
        query: {
          restartPromptEdit: linkAssetId,
        },
      };
    }

    if (isReframe && linkAssetId) {
      return {
        pathname: "/expand",
        query: {
          restartExpand: linkAssetId,
        },
      };
    }

    if (is3D && linkJobId) {
      return {
        pathname: "/3d/new",
        query: { restartGeneration: linkJobId },
      };
    }

    if (isVideo && linkJobId) {
      if (asset?.metadata.type === "video2video") {
        if (!model && !modelData) {
          return undefined;
        } else if (
          (model ?? modelData?.model)?.tags.includes("video-upscale")
        ) {
          return {
            pathname: "/videos/enhance",
            query: { restartGeneration: linkJobId },
          };
        }
      }

      return {
        pathname: "/videos/new",
        query: { restartGeneration: linkJobId },
      };
    }

    return undefined;
  }, [
    isInference,
    isReframe,
    isRestyle,
    isPromptEdit,
    isSkyboxBase,
    isSkyboxUpscale,
    isTextureInference,
    isTextureUpscale,
    isUpscale,
    linkAssetId,
    linkInferenceId,
    linkModelId,
    linkJobId,
    is3D,
    isVideo,
    model,
    modelData,
    asset,
  ]);

  if (!isUnsigned && (link || isLoading)) {
    return {
      link: isLoading ? undefined : link,
      isLoading,
    };
  }

  return undefined;
}

// ------------------------------------

export function useAssetReloadPromptLink(
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined
): LinkProps["href"] | undefined {
  const hasPrompt = !!asset?.metadata?.prompt;
  const isTextureInference = getIsAssetInferenceTexture(asset);
  const isTextureUpscale = getIsAssetUpscaledTexture(asset);
  const isInference =
    (asset?.metadata.type ?? "").includes("inference") && !isTextureInference;
  const isUpscale = asset?.metadata.type === "upscale";
  const isSkybox = asset?.metadata.type === "skybox-base-360";
  const isRestyle = asset?.metadata.type === "restyle";
  const isReframe = asset?.metadata.type === "reframe";
  const isPromptEdit = asset?.metadata.type === "image-prompt-editing";
  const isVideo = getIsAssetVideo(asset);

  const linkAssetId = asset?.id;

  return useMemo(() => {
    if (!hasPrompt) {
      return undefined;
    }

    if (isSkybox) {
      return {
        pathname: "/skyboxes/new",
        query: {
          importPromptFromAssetId: linkAssetId,
        },
      };
    }

    if (isInference) {
      return {
        pathname: "/images/new",
        query: {
          importPromptFromAssetId: linkAssetId,
        },
      };
    }

    if (isUpscale) {
      return {
        pathname: "/enhance",
        query: {
          importPromptFromAssetId: linkAssetId,
        },
      };
    }

    if (isTextureInference) {
      return {
        pathname: "/textures/new",
        query: {
          importPromptFromAssetId: linkAssetId,
        },
      };
    }

    if (isTextureUpscale) {
      return {
        pathname: "/textures/enhance",
        query: {
          importPromptFromAssetId: linkAssetId,
        },
      };
    }

    if (isRestyle) {
      return {
        pathname: "/restyle",
        query: {
          importPromptFromAssetId: linkAssetId,
        },
      };
    }

    if (isReframe) {
      return {
        pathname: "/expand",
        query: {
          importPromptFromAssetId: linkAssetId,
        },
      };
    }

    if (isPromptEdit) {
      return {
        pathname: "/edit-with-prompts",
        query: {
          importPromptFromAssetId: linkAssetId,
        },
      };
    }

    if (isVideo) {
      return {
        pathname: "/videos/new",
        query: {
          importPromptFromAssetId: linkAssetId,
        },
      };
    }

    return undefined;
  }, [
    hasPrompt,
    isInference,
    isReframe,
    isRestyle,
    isPromptEdit,
    isSkybox,
    isTextureInference,
    isTextureUpscale,
    isUpscale,
    linkAssetId,
    isVideo,
  ]);
}

// ------------------------------------

export function useAssetReloadModelsLink(
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined,
  { modelId }: { modelId?: string } = {}
): LinkProps["href"] | undefined {
  const isTextureInference = getIsAssetInferenceTexture(asset);
  const isInference =
    (asset?.metadata.type ?? "").includes("inference") && !isTextureInference;

  const linkModelId = modelId ?? asset?.metadata.modelId;

  return useMemo(() => {
    if (isInference) {
      return {
        pathname: "/images/new",
        query: {
          modelId: linkModelId,
        },
      };
    }

    if (isTextureInference) {
      return {
        pathname: "/textures/new",
        query: {
          modelId: linkModelId,
        },
      };
    }

    return undefined;
  }, [isInference, isTextureInference, linkModelId]);
}

// ------------------------------------

export function useAssetRefineLink(
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined,
  { modelId }: { modelId?: string } = {}
): LinkProps["href"] | undefined {
  const isSkyboxBase = asset?.metadata.type === "skybox-base-360";
  const isSkyboxUpscale = asset?.metadata.type === "upscale-skybox";
  const isDisabled =
    !asset ||
    isSkyboxBase ||
    !(asset.editCapabilities ?? []).includes("REFINE");
  const isTexture =
    getIsAssetInferenceTexture(asset) || getIsAssetUpscaledTexture(asset);

  const linkModelId = modelId ?? asset?.metadata.modelId;

  return useMemo(() => {
    if (isDisabled) {
      return undefined;
    }

    if (isSkyboxUpscale) {
      return {
        pathname: "/skyboxes/enhance",
        query: {
          importAssetId: asset?.id,
        },
      };
    } else if (isTexture) {
      return {
        pathname: "/textures/new",
        query: {
          importAssetId: asset?.id,
        },
      };
    } else {
      return {
        pathname: "/images/new",
        query: {
          modelId: linkModelId,
          importAssetId: asset?.id,
        },
      };
    }
  }, [asset?.id, isDisabled, isSkyboxUpscale, isTexture, linkModelId]);
}

// ------------------------------------

export function useAssetUpscaleLink(
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined
): LinkProps["href"] | undefined {
  const router = useRouter();
  const isSkybox = asset?.metadata.type === "skybox-base-360";
  const isTexture =
    getIsAssetInferenceTexture(asset) || getIsAssetUpscaledTexture(asset);
  const isVideo = getIsAssetVideo(asset);

  const isDisabled =
    (!asset ||
      asset?.metadata.type === "texture" ||
      (asset?.metadata.type ?? "").startsWith("texture-") ||
      ["upscale-skybox"].includes(asset?.metadata.type ?? "") ||
      (asset && !(asset.editCapabilities ?? []).includes("UPSCALE"))) &&
    !isVideo;

  return useMemo(() => {
    if (isDisabled) {
      return undefined;
    }

    if (isSkybox && router.pathname === "/skyboxes/new") {
      return {
        pathname: "/skyboxes/new",
        query: { enhanceAssetId: asset?.id },
      };
    } else if (isSkybox) {
      return {
        pathname: "/skyboxes/enhance",
        query: {
          importAssetId: asset?.id,
        },
      };
    } else if (isTexture) {
      return {
        pathname: "/textures/enhance",
        query: {
          importAssetId: asset?.id,
        },
      };
    } else if (isVideo) {
      return {
        pathname: "/videos/enhance",
        query: {
          importAssetId: asset?.id,
        },
      };
    } else {
      return {
        pathname: "/enhance",
        query: {
          importAssetId: asset?.id,
        },
      };
    }
  }, [asset?.id, isDisabled, isSkybox, isTexture, router.pathname, isVideo]);
}

// ------------------------------------

export function getModelIdForCanvas(
  modelId: string,
  { isLive = false }: { isLive?: boolean } = {}
) {
  const model = {
    id: modelId,
  } as GetModelsByModelIdApiResponse["model"];
  if (
    getIsModelFluxPro(model) ||
    getIsModelFluxPro1_1(model) ||
    getIsModelFluxPro1_1Ultra(model) ||
    getIsModelGptImage(model) ||
    PROMPT_EDIT_MODELS.includes(model.id as PromptEditModel)
  ) {
    return isLive ? "flux.1-schnell" : "flux.1-dev";
  }
  if (getIsModelFluxDev(model) && isLive) {
    return "flux.1-schnell";
  }
  return modelId;
}

export function useAssetCanvasLink(
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined,
  { isLive = false }: { isLive?: boolean } = {}
): LinkProps["href"] | undefined {
  const is3D = getIsAsset3D(asset);
  const isVideo = getIsAssetVideo(asset);
  const isDisabled = !asset || is3D || isVideo;

  const modelId = asset?.metadata.modelId
    ? getModelIdForCanvas(asset.metadata.modelId, { isLive })
    : undefined;

  return useMemo(() => {
    if (isDisabled) {
      return undefined;
    }

    return {
      pathname: "/canvas/new",
      query: {
        importAssetId: asset?.id,
        ...(isLive ? { live: "true" } : {}),
        ...(modelId ? { modelId } : {}),
      },
    };
  }, [asset?.id, isDisabled, isLive, modelId]);
}

export function useAssetExpandLink(
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined
): LinkProps["href"] | undefined {
  const isDisabled =
    !asset || !(asset.editCapabilities ?? []).includes("REFRAME");

  return useMemo(() => {
    if (isDisabled) {
      return undefined;
    }

    return {
      pathname: "/expand",
      query: {
        importAssetId: asset?.id,
      },
    };
  }, [asset?.id, isDisabled]);
}

export function useAssetPromptEditLink(
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined
): LinkProps["href"] | undefined {
  const isSkybox = getIsAssetSkybox(asset);
  const isTexture = getIsAssetTexture(asset);
  const isDisabled =
    !asset ||
    isSkybox ||
    isTexture ||
    !(asset.editCapabilities ?? []).includes("PROMPT_EDITING");

  return useMemo(() => {
    if (isDisabled) {
      return undefined;
    }

    return {
      pathname: "/edit-with-prompts",
      query: {
        importAssetId: asset?.id,
      },
    };
  }, [asset?.id, isDisabled]);
}

export function useAssetConvertTo3DLink(
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined
): LinkProps["href"] | undefined {
  const isSkybox = getIsAssetSkybox(asset);
  const isTexture = getIsAssetTexture(asset);
  const is3D = getIsAsset3D(asset);
  const isVideo = getIsAssetVideo(asset);
  const isDisabled = !asset || isSkybox || isTexture || is3D || isVideo;

  return useMemo(() => {
    if (isDisabled) {
      return undefined;
    }

    return {
      pathname: "/3d/new",
      query: {
        importAssetId: asset?.id,
      },
    };
  }, [asset?.id, isDisabled]);
}

export function useAssetConvertToVideoLink(
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined
): LinkProps["href"] | undefined {
  const isTexture = getIsAssetTexture(asset);
  const is3D = getIsAsset3D(asset);
  const isVideo = getIsAssetVideo(asset);
  const isDisabled = !asset || isTexture || is3D || isVideo;

  return useMemo(() => {
    if (isDisabled) {
      return undefined;
    }

    return {
      pathname: "/videos/new",
      query: {
        importAssetId: asset?.id,
      },
    };
  }, [asset?.id, isDisabled]);
}
